import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import api from '@/lib/api';

const CartContext = createContext(null);
const CART_TOKEN_KEY = 'cloudstore_cart_token';

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be inside CartProvider');
  return ctx;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const tokenRef = useRef(localStorage.getItem(CART_TOKEN_KEY));

  const fetchCart = useCallback(async (token) => {
    if (!token) return;
    try {
      const { data } = await api.get(`/cart/${token}`);
      if (data.converted_order_id) {
        localStorage.removeItem(CART_TOKEN_KEY);
        tokenRef.current = null;
        setCart(null);
        return;
      }
      setCart(data);
    } catch {
      localStorage.removeItem(CART_TOKEN_KEY);
      tokenRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (tokenRef.current) fetchCart(tokenRef.current);
  }, [fetchCart]);

  const ensureCart = useCallback(async () => {
    if (tokenRef.current) return tokenRef.current;
    const { data } = await api.post('/cart');
    tokenRef.current = data.session_token;
    localStorage.setItem(CART_TOKEN_KEY, data.session_token);
    setCart(data);
    return data.session_token;
  }, []);

  const recoverCart = useCallback(async (sessionToken) => {
    try {
      const { data } = await api.get(`/cart/recover/${sessionToken}`);
      tokenRef.current = sessionToken;
      localStorage.setItem(CART_TOKEN_KEY, sessionToken);
      setCart(data);
      return true;
    } catch {
      return false;
    }
  }, []);

  const addItem = useCallback(async (product, variant = null, quantity = 1, bundleId = '') => {
    setLoading(true);
    try {
      const token = await ensureCart();
      const { data } = await api.post(`/cart/${token}/items`, {
        product_handle: product.handle,
        variant_id: variant?.id || '',
        quantity,
        bundle_id: bundleId || '',
      });
      setCart(data);
    } catch (err) {
      console.error('Add to cart failed:', err);
    }
    setLoading(false);
  }, [ensureCart]);

  const removeItem = useCallback(async (itemId) => {
    if (!tokenRef.current) return;
    setLoading(true);
    try {
      const { data } = await api.delete(`/cart/${tokenRef.current}/items/${itemId}`);
      setCart(data);
    } catch (err) {
      console.error('Remove failed:', err);
    }
    setLoading(false);
  }, []);

  const updateQuantity = useCallback(async (itemId, qty) => {
    if (!tokenRef.current) return;
    setLoading(true);
    try {
      if (qty <= 0) {
        const { data } = await api.delete(`/cart/${tokenRef.current}/items/${itemId}`);
        setCart(data);
      } else {
        const { data } = await api.put(`/cart/${tokenRef.current}/items/${itemId}`, { quantity: qty });
        setCart(data);
      }
    } catch (err) {
      console.error('Update failed:', err);
    }
    setLoading(false);
  }, []);

  const applyCoupon = useCallback(async (code) => {
    if (!tokenRef.current) return { error: 'No cart' };
    try {
      const { data } = await api.post(`/cart/${tokenRef.current}/coupon`, { code });
      setCart(data);
      return { success: true };
    } catch (err) {
      return { error: err.response?.data?.detail || 'Invalid coupon' };
    }
  }, []);

  const removeCoupon = useCallback(async () => {
    if (!tokenRef.current) return;
    try {
      const { data } = await api.delete(`/cart/${tokenRef.current}/coupon`);
      setCart(data);
    } catch (err) {
      console.error('Remove coupon failed:', err);
    }
  }, []);

  const clearCart = useCallback(() => {
    localStorage.removeItem(CART_TOKEN_KEY);
    tokenRef.current = null;
    setCart(null);
  }, []);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  const items = cart?.items || [];
  const subtotal = cart?.subtotal || 0;
  const total = cart?.total || 0;
  const shipping = cart?.shipping || 0;
  const bundleDiscount = cart?.bundle_discount || 0;
  const couponDiscount = cart?.coupon_discount || 0;
  const coupon = cart?.coupon || null;
  const freeShipping = cart?.free_shipping || false;
  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const sessionToken = tokenRef.current;

  return (
    <CartContext.Provider value={{
      cart, items, isOpen, loading, addItem, removeItem, updateQuantity,
      applyCoupon, removeCoupon, clearCart, openCart, closeCart, recoverCart,
      subtotal, total, shipping, bundleDiscount, couponDiscount, coupon, freeShipping,
      itemCount, sessionToken,
    }}>
      {children}
    </CartContext.Provider>
  );
};
